import type { DesignModel, DistributionGroup } from '@forgd/contract/types'
import type { Ref } from 'vue'
import { isEqual } from 'ohash/utils'

export type WorkingGroup = DistributionGroup & { id: string }

export function useWorkingGroups(model: Ref<DesignModel>) {
  function stripVisual(group: DistributionGroup & { color?: string }): DistributionGroup {
    const { color: _color, ...rest } = group
    return rest
  }

  const mintedIds = new Set<string>()

  function withIds(groups: DistributionGroup[]): WorkingGroup[] {
    return groups.map((group) => {
      if (group.id) {
        return { ...stripVisual(group), id: group.id }
      }

      const id = crypto.randomUUID().toString()
      mintedIds.add(id)

      return { ...stripVisual(group), id }
    })
  }

  function toModel(groups: WorkingGroup[]): DistributionGroup[] {
    return groups.map(group => ({
      ...stripVisual(group),
      id: mintedIds.has(group.id) ? null : group.id,
    }))
  }

  const groups = ref<WorkingGroup[]>(withIds(model.value.groups))

  let lastWritten: DistributionGroup[] = toModel(groups.value)

  watch(
    groups,
    (next) => {
      const written = toModel(next)
      lastWritten = written
      model.value = { ...model.value, groups: written }
    },
    { deep: true },
  )

  watch(
    () => model.value.groups,
    (next) => {
      if (isEqual(next, lastWritten)) {
        return
      }

      groups.value = withIds(next)
    },
    { deep: true },
  )

  function addGroup(seed: Partial<DistributionGroup> = {}) {
    groups.value = [
      ...groups.value,
      { ...seed, id: crypto.randomUUID().toString() } as WorkingGroup,
    ]
  }

  function deleteGroup(id: string) {
    groups.value = groups.value.filter(group => group.id !== id)
  }

  function replaceGroups(next: DistributionGroup[]) {
    groups.value = withIds(next)
  }

  function patchGroup(id: string, patch: Partial<DistributionGroup>) {
    groups.value = groups.value.map(group =>
      group.id === id ? { ...group, ...patch } as WorkingGroup : group,
    )
  }

  return {
    groups,
    addGroup,
    deleteGroup,
    replaceGroups,
    patchGroup,
  }
}
